import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { HelmetProvider } from 'react-helmet-async';

import Navbar from "./components/Navbar";
import RequireAuth from "./components/RequireAuth";
import ChatBot from "./components/ChatBot"; // [NEW] AI ChatBot

// Lazy Loaded Components
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PatientDetail = lazy(() => import("./pages/PatientDetail"));
const PrescriptionForm = lazy(() => import("./pages/PrescriptionForm"));
const PatientHistoryPage = lazy(() => import("./pages/PatientHistoryPage"));
const VisitViewPage = lazy(() => import("./pages/VisitViewPage"));
const PrescriptionSuccessPage = lazy(() => import("./pages/PrescriptionSuccessPage"));
const DoctorDashboard = lazy(() => import("./pages/DoctorDashboard"));
const NewLandingPage = lazy(() => import("./pages/NewLandingPage"));
const PatientLogin = lazy(() => import("./pages/PatientLogin"));
const PatientPortal = lazy(() => import("./pages/PatientPortal"));
const DoctorProfile = lazy(() => import("./pages/DoctorProfile"));
const PatientProfile = lazy(() => import("./pages/PatientProfile"));
const BookVisit = lazy(() => import("./pages/BookVisit"));
const Appointments = lazy(() => import("./pages/Appointments"));
const DoctorAvailability = lazy(() => import("./pages/DoctorAvailability"));
const EmergencyAccessPage = lazy(() => import("./pages/EmergencyAccessPage"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const SetupAdmin = lazy(() => import("./pages/SetupAdmin"));
const FamilyManager = lazy(() => import("./pages/FamilyManager"));


// Loading Spinner for Suspense
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
  </div>
);

function LayoutWithConditionalNavbar({ children }) {
  const location = useLocation();
  // Hide default navbar on:
  // - Landing page (has its own custom navbar)
  // - Patient routes that have their own PatientNavbar (portal, profile, book)
  const hideNavbar =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/verify-email" ||
    location.pathname.startsWith("/patient/portal") ||
    location.pathname.startsWith("/patient/profile") ||
    location.pathname.startsWith("/patient/book") ||
    location.pathname.startsWith("/emergency-access");

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />
      {!hideNavbar && <Navbar />}
      {children}
      <ChatBot /> {/* AI ChatBot Overlay */}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <LayoutWithConditionalNavbar>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<NewLandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/patient/login" element={<PatientLogin />} />
              <Route path="/patient/profile" element={<PatientProfile />} />
              <Route path="/patient/family-manager" element={<FamilyManager />} />
              <Route path="/patient/book" element={<BookVisit />} />
              <Route path="/emergency-access/:tokenId" element={<EmergencyAccessPage />} />
              <Route path="/setup-admin" element={<SetupAdmin />} />

              {/* Patient Portal (public but user-validated) */}
              <Route path="/patient/portal" element={<PatientPortal />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <RequireAuth>
                    <Dashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/patient/new"
                element={
                  <RequireAuth>
                    <PatientDetail />
                  </RequireAuth>
                }
              />
              <Route
                path="/patient/:id"
                element={
                  <RequireAuth>
                    <PatientDetail />
                  </RequireAuth>
                }
              />
              <Route
                path="/patient/:id/history"
                element={
                  <RequireAuth>
                    <PatientHistoryPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/prescribe/:id"
                element={
                  <RequireAuth>
                    <PrescriptionForm />
                  </RequireAuth>
                }
              />
              <Route
                path="/visit/:id"
                element={
                  <RequireAuth>
                    <VisitViewPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/prescription/success/:visitId"
                element={
                  <RequireAuth>
                    <PrescriptionSuccessPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/analytics"
                element={
                  <RequireAuth>
                    <DoctorDashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/doctor/profile"
                element={
                  <RequireAuth>
                    <DoctorProfile />
                  </RequireAuth>
                }
              />

              <Route
                path="/appointments"
                element={
                  <RequireAuth>
                    <Appointments />
                  </RequireAuth>
                }
              />
              <Route
                path="/doctor/availability"
                element={
                  <RequireAuth>
                    <DoctorAvailability />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin"
                element={
                  <RequireAuth allowedRoles={["admin"]}>
                    <AdminDashboard />
                  </RequireAuth>
                }
              />
            </Routes>
          </Suspense>
        </LayoutWithConditionalNavbar>
      </Router>
    </HelmetProvider>
  );
}

export default App;
